import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowLeft, CalendarDays, MapPin, Store } from 'lucide-react';
import SiteHeader from '@/components/SiteHeader';
import SiteFooter from '@/components/SiteFooter';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { RichTextRenderer, Seo } from '@/components/common';
import { useFleaMarketOccurrenceGalleryUrlMap } from '@/hooks/useFleaMarketOccurrenceMedia';
import { usePublicFleaMarketDetail, usePublicFleaMarketOccurrences } from '@/hooks/usePublicFleaMarkets';
import {
  formatOccurrenceDateShortSv,
  formatOccurrenceTimeRangeSv,
  normalizeOccurrenceDescriptionOverride,
  resolveOccurrenceImages,
} from '@/lib/fleaMarkets/occurrences';

export default function FleaMarketOccurrenceDetailPage() {
  const { fleaMarketId, occurrenceId } = useParams<{ fleaMarketId: string; occurrenceId: string }>();

  const { data: detail, isLoading: detailLoading } = usePublicFleaMarketDetail(fleaMarketId);
  const { data: occurrences, isLoading: occLoading } = usePublicFleaMarketOccurrences(fleaMarketId);
  const { data: occurrenceImageMap = {} } = useFleaMarketOccurrenceGalleryUrlMap(occurrenceId ? [occurrenceId] : []);

  const isLoading = detailLoading || occLoading;
  const occurrence = (occurrences ?? []).find((item) => item.id === occurrenceId) ?? null;

  const overrideHtml = occurrence
    ? normalizeOccurrenceDescriptionOverride(occurrence.description_override) ??
      normalizeOccurrenceDescriptionOverride(occurrence.note)
    : null;

  const occurrenceImages = occurrence
    ? occurrenceImageMap[occurrence.id]?.length
      ? occurrenceImageMap[occurrence.id]
      : resolveOccurrenceImages(occurrence)
    : [];

  const hasMeaningfulContent = Boolean(overrideHtml) || occurrenceImages.length > 0;

  if (!isLoading && (!detail || !occurrence)) {
    return <Navigate to="/explore" replace />;
  }

  if (!isLoading && detail && occurrence && !hasMeaningfulContent) {
    return <Navigate to={`/loppis/${detail.flea_market_id}`} replace />;
  }

  if (isLoading || !detail || !occurrence) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <SiteHeader density="compact" />
        <main className="flex-1 container py-8">
          <Skeleton className="h-5 w-24 mb-6" />
          <Skeleton className="h-8 w-1/2 mb-3" />
          <Skeleton className="h-5 w-1/3 mb-2" />
          <Skeleton className="h-40 w-full max-w-3xl rounded-xl" />
        </main>
        <SiteFooter />
      </div>
    );
  }

  const canonicalUrl = `${window.location.origin}/loppis/${detail.flea_market_id}/tillfalle/${occurrence.id}`;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Seo
        title={`${detail.title} – Tillfälle`}
        description={`Del av ${detail.title} hos ${detail.producer_name}`}
        canonicalUrl={canonicalUrl}
        ogType="article"
      />

      <SiteHeader density="compact" />

      <main className="flex-1 container py-6 md:py-10">
        <div className="max-w-3xl space-y-4">
          <Button variant="ghost" size="sm" className="mb-1" asChild>
            <Link to={`/loppis/${detail.flea_market_id}`}>
              <ArrowLeft className="h-4 w-4 mr-1" />
              Tillbaka till loppisen
            </Link>
          </Button>

          <p className="text-xs text-muted-foreground">Del av {detail.title}</p>

          <h1 className="font-display text-2xl md:text-3xl text-foreground">{detail.title}</h1>

          <div className="rounded-xl border border-border/60 bg-card p-5">
            <div className="flex items-center gap-2 mb-3">
              <CalendarDays className="h-4 w-4 text-primary" />
              <h2 className="font-medium text-foreground">Tillfälle</h2>
            </div>
            <p className="text-base font-semibold text-foreground leading-snug">
              {formatOccurrenceDateShortSv(occurrence.start_at)}
            </p>
            <p className="text-sm text-muted-foreground mt-0.5">
              {formatOccurrenceTimeRangeSv(occurrence.start_at, occurrence.end_at)}
            </p>
          </div>

          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <Store className="h-4 w-4" />
              {detail.producer_slug ? (
                <Link to={`/producer/${detail.producer_slug}`} className="hover:underline">
                  {detail.producer_name}
                </Link>
              ) : (
                detail.producer_name
              )}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="h-4 w-4" />
              {detail.place_slug ? (
                <Link to={`/place/${detail.place_slug}`} className="hover:underline">
                  {detail.place_name}
                </Link>
              ) : (
                detail.place_name
              )}
            </span>
          </div>

          {overrideHtml && (
            <section className="rounded-xl border border-border/60 bg-card p-5">
              <h2 className="font-medium text-foreground mb-2">Om detta tillfälle</h2>
              <div className="prose prose-neutral max-w-none prose-p:my-3 prose-ul:my-3 prose-li:my-1 prose-hr:my-6">
                <RichTextRenderer html={overrideHtml} />
              </div>
            </section>
          )}

          {occurrenceImages.length > 0 && (
            <section className="rounded-xl border border-border/60 bg-card p-5">
              <h2 className="font-medium text-foreground mb-3">Bilder från detta tillfälle</h2>
              <div className="grid grid-cols-2 gap-2">
                {occurrenceImages.map((url) => (
                  <img
                    key={`${occurrence.id}-${url}`}
                    src={url}
                    alt={detail.title}
                    className="h-32 md:h-40 w-full object-cover rounded-md border border-border/40"
                    loading="lazy"
                  />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
